import { defineMessages } from '@/i18n'

/** Fleet map: vehicles where they were last known to be, project sites (docs/fleet-design.md §Map). */
export const fleetMapMessages = defineMessages({
  en: {
    title: 'Fleet map',
    subtitle: 'Where each vehicle was last known to be, and on what grounds.',
    viewList: 'List',
    viewMap: 'Map',
    loading: 'Loading map',
    search: 'Plate, driver, crew, project…',
    onMap: '{n} on the map',
    noPosition: '{n} without a position',
    noPositionHint: 'Not yet checked out or in with GPS, not out on a project with a site, and not placed at the home yard.',
    srcTracker: 'Tracker',
    srcLive: 'Live',
    srcCheckout: 'Where it was taken (GPS)',
    srcReturned: 'Where it was brought back (GPS)',
    srcSite: 'At its project site',
    srcAu: 'Last AU check-out (GPS)',
    srcYard: 'At the home yard',
    siteManual: 'pinned',
    siteRtk: 'RTK centre',
    siteGeocoded: 'town',
    siteYard: 'home yard',
    legendExact: 'GPS position',
    legendApprox: 'Approximate: project site',
    legendSite: 'Project site',
    legendYard: 'Home yard (no live GPS)',
    moreHere: '{n} more — open the list',
    yardHere: '{n} at the yard',
    trackerOff: 'No live tracking connected: last AU GPS, the home yard, GPS at check-out/in, or the project site.',
    trackerOn: 'Live GPS connected (same tracker as AU): last tracker fix, else the last known one.',
    kindAll: 'All types',
    kindDigger: 'Digger',
    kindVan: 'Van',
    kindCar: 'Car',
    kindTruck: 'Truck',
    kindPickup: 'Pickup',
    kindRoller: 'Roller',
    kindTrailer: 'Trailer',
    kindOther: 'Other',
    motionAll: 'All',
    moving: 'Moving',
    parked: 'Parked',
    legendMoving: 'Moving (red)',
    legendParked: 'Parked (blue)',
    speedKmh: '{n} km/h',
    showTracksHint: 'Tap a vehicle to draw its GPS trail (yesterday and today).',
    noTrack: 'No GPS trail for this vehicle in the last two days.',
    loadingTrack: 'Loading trail…',
    trackReady: '{n} GPS points',
    sites: 'Project sites',
    sitesSummary: '{placed} of {total} projects on the map',
    sourceManual: 'Pinned',
    sourceRtk: 'RTK centre',
    sourceGeocoded: 'Town (looked up)',
    sourceUnfound: 'Town not found',
    sourceNone: 'No site yet',
    lookup: 'Look up towns ({n})',
    lookupHint: "Finds each project's town on OpenStreetMap from its name, about one a second. A wrong guess: move the pin.",
    lookingUp: 'Looking up towns… {left} left',
    lookupDone: '{found} found, {missed} not found.',
    lookupFailed: "The town lookup didn't answer. Try again later.",
    needsSite: 'Needs a site',
    pinPrompt: 'Click the map where {name} is.',
    pin: 'Set on map',
    movePin: 'Move',
    removePin: 'Remove pin',
    cancel: 'Cancel',
    vehiclesHere: '{n} vehicles',
    outOn: 'Out on {project}',
    fixLocating: 'Finding your position…',
    fixOk: 'Position recorded (±{m} m)',
    fixNone: 'No position (not allowed or unavailable). That is fine, it is optional.',
    showOnMap: 'Show on map',
    openList: 'Vehicle list',
    mapFilters: 'Vehicles',
    collapse: 'Collapse',
    collapsePanel: 'Collapse panel',
    expandPanel: 'Expand panel',
    closePanel: 'Close panel',
    liveNow: 'Live now',
    detail: 'Vehicle',
    clickVehicleHint: 'Tap a vehicle on the map or in the list.',
    showTracks: 'Show trails',
    playHistory: 'Play the trip',
    replayAt: '{time}',
    pause: 'Pause',
    surveyDay: 'Fleet day',
    bubbleSuffix: '{metres} positions · day {day}/{days}',
    dayTitle: {
      one: '{date} — {count} vehicle, {metres} positions',
      other: '{date} — {count} vehicles, {metres} positions',
    },
    gapNote: {
      one: '{count} day since the previous movement',
      other: '{count} days since the previous movement',
    },
    sliderValue: '{date}, {metres} positions so far',
  },
  de: {
    title: 'Fuhrpark-Karte',
    subtitle: 'Wo jedes Fahrzeug zuletzt bekannt war, und woher wir das wissen.',
    viewList: 'Liste',
    viewMap: 'Karte',
    loading: 'Karte wird geladen',
    search: 'Kennzeichen, Fahrer, Kolonne, Projekt…',
    onMap: '{n} auf der Karte',
    noPosition: '{n} ohne Position',
    noPositionHint: 'Noch nicht mit GPS aus- oder zurückgegeben, nicht auf einem Projekt mit Baustelle, und nicht am Betriebshof.',
    srcTracker: 'Tracker',
    srcLive: 'Live',
    srcCheckout: 'Wo es abgeholt wurde (GPS)',
    srcReturned: 'Wo es zurückgegeben wurde (GPS)',
    srcSite: 'Auf seiner Baustelle',
    srcAu: 'Letzter AU-Checkout (GPS)',
    srcYard: 'Am Betriebshof',
    siteManual: 'gesetzt',
    siteRtk: 'RTK-Mitte',
    siteGeocoded: 'Ort',
    siteYard: 'Betriebshof',
    legendExact: 'GPS-Position',
    legendApprox: 'Ungefähr: Baustelle',
    legendSite: 'Baustelle',
    legendYard: 'Betriebshof (kein Live-GPS)',
    moreHere: 'noch {n} — Liste öffnen',
    yardHere: '{n} am Hof',
    trackerOff: 'Kein Live-Tracking angebunden: letzter AU-GPS, der Betriebshof, GPS bei Aus-/Rückgabe oder die Baustelle.',
    trackerOn: 'Live-GPS angebunden (gleicher Tracker wie AU): letzte Tracker-Position, sonst die zuletzt bekannte.',
    kindAll: 'Alle Typen',
    kindDigger: 'Bagger',
    kindVan: 'Transporter',
    kindCar: 'Pkw',
    kindTruck: 'Lkw',
    kindPickup: 'Pickup',
    kindRoller: 'Walze',
    kindTrailer: 'Anhänger',
    kindOther: 'Sonstige',
    motionAll: 'Alle',
    moving: 'Unterwegs',
    parked: 'Steht',
    legendMoving: 'Unterwegs (rot)',
    legendParked: 'Steht (blau)',
    speedKmh: '{n} km/h',
    showTracksHint: 'Fahrzeug antippen, um die GPS-Spur von gestern und heute zu zeichnen.',
    noTrack: 'Keine GPS-Spur für dieses Fahrzeug in den letzten zwei Tagen.',
    loadingTrack: 'Spur wird geladen…',
    trackReady: '{n} GPS-Punkte',
    sites: 'Baustellen',
    sitesSummary: '{placed} von {total} Projekten auf der Karte',
    sourceManual: 'Gesetzt',
    sourceRtk: 'RTK-Mitte',
    sourceGeocoded: 'Ort (gesucht)',
    sourceUnfound: 'Ort nicht gefunden',
    sourceNone: 'Noch keine Baustelle',
    lookup: 'Orte suchen ({n})',
    lookupHint: 'Sucht den Ort jedes Projekts aus seinem Namen in OpenStreetMap, etwa einen pro Sekunde. Falsch geraten: Pin verschieben.',
    lookingUp: 'Orte werden gesucht… noch {left}',
    lookupDone: '{found} gefunden, {missed} nicht gefunden.',
    lookupFailed: 'Die Ortssuche hat nicht geantwortet. Später erneut versuchen.',
    needsSite: 'Braucht eine Baustelle',
    pinPrompt: 'Auf die Karte klicken, wo {name} liegt.',
    pin: 'Auf Karte setzen',
    movePin: 'Verschieben',
    removePin: 'Pin entfernen',
    cancel: 'Abbrechen',
    vehiclesHere: '{n} Fahrzeuge',
    outOn: 'Unterwegs auf {project}',
    fixLocating: 'Position wird ermittelt…',
    fixOk: 'Position erfasst (±{m} m)',
    fixNone: 'Keine Position (nicht erlaubt oder nicht verfügbar). Kein Problem, sie ist freiwillig.',
    showOnMap: 'Auf der Karte',
    openList: 'Fahrzeugliste',
    mapFilters: 'Fahrzeuge',
    collapse: 'Einklappen',
    collapsePanel: 'Panel einklappen',
    expandPanel: 'Panel ausklappen',
    closePanel: 'Panel schließen',
    liveNow: 'Jetzt live',
    detail: 'Fahrzeug',
    clickVehicleHint: 'Fahrzeug auf der Karte oder in der Liste antippen.',
    showTracks: 'Spuren zeigen',
    playHistory: 'Fahrt abspielen',
    replayAt: '{time}',
    pause: 'Pause',
    surveyDay: 'Fuhrparktag',
    bubbleSuffix: '{metres} Positionen · Tag {day}/{days}',
    dayTitle: {
      one: '{date} — {count} Fahrzeug, {metres} Positionen',
      other: '{date} — {count} Fahrzeuge, {metres} Positionen',
    },
    gapNote: {
      one: '{count} Tag seit der letzten Bewegung',
      other: '{count} Tage seit der letzten Bewegung',
    },
    sliderValue: '{date}, {metres} Positionen bisher',
  },
  cnr: {
    title: 'Karta voznog parka',
    subtitle: 'Gdje je svako vozilo posljednji put bilo poznato, i na osnovu čega.',
    viewList: 'Lista',
    viewMap: 'Karta',
    loading: 'Učitavanje karte',
    search: 'Tablice, vozač, kolona, projekat…',
    onMap: '{n} na karti',
    noPosition: '{n} bez pozicije',
    noPositionHint: 'Još nije zaduženo ni razduženo s GPS-om, nije na projektu s gradilištem, i nije na dvorištu.',
    srcTracker: 'Tracker',
    srcLive: 'Uživo',
    srcCheckout: 'Gdje je preuzeto (GPS)',
    srcReturned: 'Gdje je vraćeno (GPS)',
    srcSite: 'Na gradilištu projekta',
    srcAu: 'Posljednje AU zaduženje (GPS)',
    srcYard: 'Na dvorištu',
    siteManual: 'postavljeno',
    siteRtk: 'RTK centar',
    siteGeocoded: 'mjesto',
    siteYard: 'dvorište',
    legendExact: 'GPS pozicija',
    legendApprox: 'Približno: gradilište',
    legendSite: 'Gradilište',
    legendYard: 'Dvorište (nema GPS uživo)',
    moreHere: 'još {n} — otvori listu',
    yardHere: '{n} na dvorištu',
    trackerOff: 'Praćenje uživo nije povezano: posljednji AU GPS, dvorište, GPS pri zaduženju/razduženju, ili gradilište.',
    trackerOn: 'GPS uživo je povezan (isti treker kao AU): posljednja treker pozicija, inače posljednja poznata.',
    kindAll: 'Svi tipovi',
    kindDigger: 'Bager',
    kindVan: 'Kombi',
    kindCar: 'Auto',
    kindTruck: 'Kamion',
    kindPickup: 'Pikap',
    kindRoller: 'Valjak',
    kindTrailer: 'Prikolica',
    kindOther: 'Ostalo',
    motionAll: 'Sva',
    moving: 'U pokretu',
    parked: 'Parkirano',
    legendMoving: 'U pokretu (crveno)',
    legendParked: 'Parkirano (plavo)',
    speedKmh: '{n} km/h',
    showTracksHint: 'Dodirnite vozilo da se nacrta GPS trag (juče i danas).',
    noTrack: 'Nema GPS traga za ovo vozilo u poslednja dva dana.',
    loadingTrack: 'Učitavanje traga…',
    trackReady: '{n} GPS tačaka',
    sites: 'Gradilišta',
    sitesSummary: '{placed} od {total} projekata na karti',
    sourceManual: 'Postavljeno',
    sourceRtk: 'RTK centar',
    sourceGeocoded: 'Mjesto (pronađeno)',
    sourceUnfound: 'Mjesto nije pronađeno',
    sourceNone: 'Još bez gradilišta',
    lookup: 'Pronađi mjesta ({n})',
    lookupHint: 'Traži mjesto svakog projekta po nazivu na OpenStreetMap-u, otprilike jedno u sekundi. Pogrešno: pomjeri pin.',
    lookingUp: 'Traženje mjesta… još {left}',
    lookupDone: '{found} pronađeno, {missed} nije pronađeno.',
    lookupFailed: 'Pretraga mjesta nije odgovorila. Pokušajte kasnije.',
    needsSite: 'Treba gradilište',
    pinPrompt: 'Kliknite na kartu gdje je {name}.',
    pin: 'Postavi na kartu',
    movePin: 'Pomjeri',
    removePin: 'Ukloni pin',
    cancel: 'Otkaži',
    vehiclesHere: '{n} vozila',
    outOn: 'Na projektu {project}',
    fixLocating: 'Traženje pozicije…',
    fixOk: 'Pozicija zabilježena (±{m} m)',
    fixNone: 'Bez pozicije (nije dozvoljeno ili nije dostupno). U redu je, nije obavezno.',
    showOnMap: 'Na karti',
    openList: 'Lista vozila',
    mapFilters: 'Vozila',
    collapse: 'Skupi',
    collapsePanel: 'Skupi panel',
    expandPanel: 'Otvori panel',
    closePanel: 'Zatvori panel',
    liveNow: 'Uživo sada',
    detail: 'Vozilo',
    clickVehicleHint: 'Dodirnite vozilo na karti ili na listi.',
    showTracks: 'Prikaži tragove',
    playHistory: 'Pusti vožnju',
    replayAt: '{time}',
    pause: 'Pauza',
    surveyDay: 'Dan voznog parka',
    bubbleSuffix: '{metres} pozicija · dan {day}/{days}',
    dayTitle: {
      one: '{date} — {count} vozilo, {metres} pozicija',
      few: '{date} — {count} vozila, {metres} pozicija',
      other: '{date} — {count} vozila, {metres} pozicija',
    },
    gapNote: {
      one: '{count} dan od prethodnog kretanja',
      few: '{count} dana od prethodnog kretanja',
      other: '{count} dana od prethodnog kretanja',
    },
    sliderValue: '{date}, {metres} pozicija do sada',
  },
  bs: {
    title: 'Karta voznog parka',
    subtitle: 'Gdje je svako vozilo posljednji put bilo poznato, i na osnovu čega.',
    viewList: 'Lista',
    viewMap: 'Karta',
    loading: 'Učitavanje karte',
    search: 'Tablice, vozač, kolona, projekat…',
    onMap: '{n} na karti',
    noPosition: '{n} bez pozicije',
    noPositionHint: 'Još nije zaduženo ni razduženo s GPS-om, nije na projektu s gradilištem, i nije na dvorištu.',
    srcTracker: 'Tracker',
    srcLive: 'Uživo',
    srcCheckout: 'Gdje je preuzeto (GPS)',
    srcReturned: 'Gdje je vraćeno (GPS)',
    srcSite: 'Na gradilištu projekta',
    srcAu: 'Posljednje AU zaduženje (GPS)',
    srcYard: 'Na dvorištu',
    siteManual: 'postavljeno',
    siteRtk: 'RTK centar',
    siteGeocoded: 'mjesto',
    siteYard: 'dvorište',
    legendExact: 'GPS pozicija',
    legendApprox: 'Približno: gradilište',
    legendSite: 'Gradilište',
    legendYard: 'Dvorište (nema GPS uživo)',
    moreHere: 'još {n} — otvori listu',
    yardHere: '{n} na dvorištu',
    trackerOff: 'Praćenje uživo nije povezano: posljednji AU GPS, dvorište, GPS pri zaduženju/razduženju, ili gradilište.',
    trackerOn: 'GPS uživo je povezan (isti treker kao AU): posljednja treker pozicija, inače posljednja poznata.',
    kindAll: 'Svi tipovi',
    kindDigger: 'Bager',
    kindVan: 'Kombi',
    kindCar: 'Auto',
    kindTruck: 'Kamion',
    kindPickup: 'Pikap',
    kindRoller: 'Valjak',
    kindTrailer: 'Prikolica',
    kindOther: 'Ostalo',
    motionAll: 'Sva',
    moving: 'U pokretu',
    parked: 'Parkirano',
    legendMoving: 'U pokretu (crveno)',
    legendParked: 'Parkirano (plavo)',
    speedKmh: '{n} km/h',
    showTracksHint: 'Dodirnite vozilo da se nacrta GPS trag (juče i danas).',
    noTrack: 'Nema GPS traga za ovo vozilo u poslednja dva dana.',
    loadingTrack: 'Učitavanje traga…',
    trackReady: '{n} GPS tačaka',
    sites: 'Gradilišta',
    sitesSummary: '{placed} od {total} projekata na karti',
    sourceManual: 'Postavljeno',
    sourceRtk: 'RTK centar',
    sourceGeocoded: 'Mjesto (pronađeno)',
    sourceUnfound: 'Mjesto nije pronađeno',
    sourceNone: 'Još bez gradilišta',
    lookup: 'Pronađi mjesta ({n})',
    lookupHint: 'Traži mjesto svakog projekta po nazivu na OpenStreetMap-u, otprilike jedno u sekundi. Pogrešno: pomjeri pin.',
    lookingUp: 'Traženje mjesta… još {left}',
    lookupDone: '{found} pronađeno, {missed} nije pronađeno.',
    lookupFailed: 'Pretraga mjesta nije odgovorila. Pokušajte kasnije.',
    needsSite: 'Treba gradilište',
    pinPrompt: 'Kliknite na kartu gdje je {name}.',
    pin: 'Postavi na kartu',
    movePin: 'Pomjeri',
    removePin: 'Ukloni pin',
    cancel: 'Otkaži',
    vehiclesHere: '{n} vozila',
    outOn: 'Na projektu {project}',
    fixLocating: 'Traženje pozicije…',
    fixOk: 'Pozicija zabilježena (±{m} m)',
    fixNone: 'Bez pozicije (nije dozvoljeno ili nije dostupno). U redu je, nije obavezno.',
    showOnMap: 'Na karti',
    openList: 'Lista vozila',
    mapFilters: 'Vozila',
    collapse: 'Skupi',
    collapsePanel: 'Skupi panel',
    expandPanel: 'Otvori panel',
    closePanel: 'Zatvori panel',
    liveNow: 'Uživo sada',
    detail: 'Vozilo',
    clickVehicleHint: 'Dodirnite vozilo na karti ili na listi.',
    showTracks: 'Prikaži tragove',
    playHistory: 'Pusti vožnju',
    replayAt: '{time}',
    pause: 'Pauza',
    surveyDay: 'Dan voznog parka',
    bubbleSuffix: '{metres} pozicija · dan {day}/{days}',
    dayTitle: {
      one: '{date} — {count} vozilo, {metres} pozicija',
      few: '{date} — {count} vozila, {metres} pozicija',
      other: '{date} — {count} vozila, {metres} pozicija',
    },
    gapNote: {
      one: '{count} dan od prethodnog kretanja',
      few: '{count} dana od prethodnog kretanja',
      other: '{count} dana od prethodnog kretanja',
    },
    sliderValue: '{date}, {metres} pozicija do sada',
  },
  sr: {
    title: 'Karta voznog parka',
    subtitle: 'Gde je svako vozilo poslednji put bilo poznato, i na osnovu čega.',
    viewList: 'Lista',
    viewMap: 'Karta',
    loading: 'Učitavanje karte',
    search: 'Tablice, vozač, kolona, projekat…',
    onMap: '{n} na karti',
    noPosition: '{n} bez pozicije',
    noPositionHint: 'Još nije zaduženo ni razduženo sa GPS-om, nije na projektu sa gradilištem, i nije na dvorištu.',
    srcTracker: 'Treker',
    srcLive: 'Uživo',
    srcCheckout: 'Gde je preuzeto (GPS)',
    srcReturned: 'Gde je vraćeno (GPS)',
    srcSite: 'Na gradilištu projekta',
    srcAu: 'Poslednje AU zaduženje (GPS)',
    srcYard: 'Na dvorištu',
    siteManual: 'postavljeno',
    siteRtk: 'RTK centar',
    siteGeocoded: 'mesto',
    siteYard: 'dvorište',
    legendExact: 'GPS pozicija',
    legendApprox: 'Približno: gradilište',
    legendSite: 'Gradilište',
    legendYard: 'Dvorište (nema GPS uživo)',
    moreHere: 'još {n} — otvori listu',
    yardHere: '{n} na dvorištu',
    trackerOff: 'Praćenje uživo nije povezano: poslednji AU GPS, dvorište, GPS pri zaduženju/razduženju, ili gradilište.',
    trackerOn: 'GPS uživo je povezan (isti treker kao AU): poslednja treker pozicija, inače poslednja poznata.',
    kindAll: 'Svi tipovi',
    kindDigger: 'Bager',
    kindVan: 'Kombi',
    kindCar: 'Auto',
    kindTruck: 'Kamion',
    kindPickup: 'Pikap',
    kindRoller: 'Valjak',
    kindTrailer: 'Prikolica',
    kindOther: 'Ostalo',
    motionAll: 'Sva',
    moving: 'U pokretu',
    parked: 'Parkirano',
    legendMoving: 'U pokretu (crveno)',
    legendParked: 'Parkirano (plavo)',
    speedKmh: '{n} km/h',
    showTracksHint: 'Dodirnite vozilo da se nacrta GPS trag (juče i danas).',
    noTrack: 'Nema GPS traga za ovo vozilo u poslednja dva dana.',
    loadingTrack: 'Učitavanje traga…',
    trackReady: '{n} GPS tačaka',
    sites: 'Gradilišta',
    sitesSummary: '{placed} od {total} projekata na karti',
    sourceManual: 'Postavljeno',
    sourceRtk: 'RTK centar',
    sourceGeocoded: 'Mesto (pronađeno)',
    sourceUnfound: 'Mesto nije pronađeno',
    sourceNone: 'Još bez gradilišta',
    lookup: 'Pronađi mesta ({n})',
    lookupHint: 'Traži mesto svakog projekta po nazivu na OpenStreetMap-u, otprilike jedno u sekundi. Pogrešno: pomeri pin.',
    lookingUp: 'Traženje mesta… još {left}',
    lookupDone: '{found} pronađeno, {missed} nije pronađeno.',
    lookupFailed: 'Pretraga mesta nije odgovorila. Pokušajte kasnije.',
    needsSite: 'Treba gradilište',
    pinPrompt: 'Kliknite na kartu gde je {name}.',
    pin: 'Postavi na kartu',
    movePin: 'Pomeri',
    removePin: 'Ukloni pin',
    cancel: 'Otkaži',
    vehiclesHere: '{n} vozila',
    outOn: 'Na projektu {project}',
    fixLocating: 'Traženje pozicije…',
    fixOk: 'Pozicija zabeležena (±{m} m)',
    fixNone: 'Bez pozicije (nije dozvoljeno ili nije dostupno). U redu je, nije obavezno.',
    showOnMap: 'Na karti',
    openList: 'Lista vozila',
    mapFilters: 'Vozila',
    collapse: 'Skupi',
    collapsePanel: 'Skupi panel',
    expandPanel: 'Otvori panel',
    closePanel: 'Zatvori panel',
    liveNow: 'Uživo sada',
    detail: 'Vozilo',
    clickVehicleHint: 'Dodirnite vozilo na karti ili na listi.',
    showTracks: 'Prikaži tragove',
    playHistory: 'Pusti vožnju',
    replayAt: '{time}',
    pause: 'Pauza',
    surveyDay: 'Dan voznog parka',
    bubbleSuffix: '{metres} pozicija · dan {day}/{days}',
    dayTitle: {
      one: '{date} — {count} vozilo, {metres} pozicija',
      few: '{date} — {count} vozila, {metres} pozicija',
      other: '{date} — {count} vozila, {metres} pozicija',
    },
    gapNote: {
      one: '{count} dan od prethodnog kretanja',
      few: '{count} dana od prethodnog kretanja',
      other: '{count} dana od prethodnog kretanja',
    },
    sliderValue: '{date}, {metres} pozicija do sada',
  },
})
